import { useEffect, useState } from "react";
import styles from "./ZoomMeetingCard.module.css";
import useDashboardStore from "../../store/dashboardStore";
import useAuthStore from "../../store/authStore";
import api from "../../services/api";
import { ENDPOINTS } from "../../utils/endpoints";
import { ZoomMtg } from "@zoom/meetingsdk";
import { toast } from "react-toastify";
import { decryptPassword } from "../../utils/crypto";
import { FaSpinner } from "react-icons/fa";

const ZoomMeetingCard = () => {
  const { user, userMeta, refreshIapStatus } = useAuthStore();
  const { selectedZoomSession } = useDashboardStore();
  const [isAccessible, setIsAccessible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [joined, setJoined] = useState(false);

  // Purchase functionality states
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [showPlanSelection, setShowPlanSelection] = useState(false);
  const [availablePlans, setAvailablePlans] = useState([]);

  // Purchase functionality
  const handleMembershipClick = async () => {
    if (isPurchasing || isLoadingPlans) return;

    setIsLoadingPlans(true);

    try {
      const membershipIds = selectedZoomSession.membership_ids || [];

      if (membershipIds.length === 0) {
        toast.error("No membership plans configured for this session");
        return;
      }

      if (membershipIds.length === 1) {
        // Single plan - proceed directly
        handlePurchaseMembership(membershipIds[0]);
      } else {
        // Multiple plans - fetch all memberships and filter
        const { data: membershipsData } = await api.get(
          ENDPOINTS.GET_ALL_MEMBERSHIPS,
          { params: { hide_restricted: true } },
        );
        const allPlans = membershipsData.memberships || [];

        // Filter to only show plans that this session uses
        const sessionPlans = allPlans.filter((plan) =>
          membershipIds.includes(plan.id),
        );

        setAvailablePlans(sessionPlans);
        setShowPlanSelection(true);
      }
    } catch (error) {
      console.error("Error fetching session plans:", error);
      toast.error("Failed to load membership plans");
    } finally {
      setIsLoadingPlans(false);
    }
  };

  const handlePurchaseMembership = async (membershipPlanId) => {
    if (!membershipPlanId || isPurchasing) return;

    setIsPurchasing(true);

    try {
      const response = await api.post(ENDPOINTS.PURCHASE_LINK, {
        user_id: user.id,
        lid: membershipPlanId,
      });

      if (response.data.success) {
        window.open(response.data.redirect_url, "_blank");
      } else {
        toast.error(
          response.data.message || "Failed to generate purchase link",
        );
      }
    } catch (error) {
      console.error("Purchase error:", error);
      toast.error("Failed to initiate purchase. Please try again.");
    } finally {
      setIsPurchasing(false);
    }
  };

  useEffect(() => {
    refreshIapStatus();
  }, []);
  useEffect(() => {
    const handleMeetingPermission = async () => {
      setLoading(true);
      if (selectedZoomSession.is_paid === 1) {
        const membershipsAllowed = selectedZoomSession.membership_ids || [];
        const role = user?.roles?.[0];

        // Admin or meeting creator trader always accessible
        if (
          role === "administrator" ||
          (role === "trader" && user.id === selectedZoomSession.instructor_id)
        ) {
          setIsAccessible(true);
        }
        // ✅ IAP users get full access (paid from mobile)
        else if (userMeta?.has_active_iap === true) {
          setIsAccessible(true);
        }
        // WordPress membership check
        else {
          let userMembershipIds = [];
          if (Array.isArray(userMeta?.membership_ids)) {
            userMembershipIds = userMeta.membership_ids.map(Number);
          } else if (typeof userMeta?.membership_ids === "string") {
            try {
              userMembershipIds = JSON.parse(userMeta.membership_ids).map(
                Number,
              );
            } catch {
              userMembershipIds = [];
            }
          }

          setIsAccessible(
            membershipsAllowed.some((id) => userMembershipIds.includes(id)),
          );
        }
      } else {
        setIsAccessible(true);
      }
      setLoading(false);
    };

    if (selectedZoomSession?.id) {
      handleMeetingPermission();
    }
  }, [selectedZoomSession, userMeta]);
  const handleJoinMeeting = async () => {
    try {
      setJoined(true);

      const res = await api.post(ENDPOINTS.GET_ZOOM_MEETING_SIGNATURE, {
        meetingNumber: selectedZoomSession.zoom_link,
      });

      const { signature, meetingNumber } = res.data;

      ZoomMtg.preLoadWasm();
      ZoomMtg.prepareWebSDK();

      document.getElementById("zmmtg-root").style.display = "block";

      ZoomMtg.init({
        leaveUrl: "https://app.eaugroup.co/",

        // leaveUrl: "https://173.214.164.83/",
        patchJsMedia: true,
        disableInvite: true,
        leaveOnPageUnload: true,
        meetingInfo: [],
        showMeetingHeader: false,
        screenShare: false,
        disablePreview: true,

        success: (success) => {
          console.log("SDK initialized successfully");

          ZoomMtg.join({
            signature: signature,
            meetingNumber: meetingNumber,
            userName: `${user.first_name} ${user.last_name}` || "Guest",
            userEmail: "",
            passWord: decryptPassword(selectedZoomSession.zoom_password),
            success: (success) => {
              console.log("Joined meeting successfully");
            },
            error: (error) => {
              // console.error("Failed to join meeting:", error);
            },
          });
        },
        error: (error) => {
          // console.error("Failed to initialize SDK:", error);
        },
      });
    } catch (err) {
      // console.error("Failed to join meeting", err);
      toast.error("Could not join meeting. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className={styles.chatContainer}>
        <div className={styles.restrictedContainer}>Checking access...</div>
      </div>
    );
  }

  if (!userMeta) {
    return (
      <div className={styles.chatContainer}>
        <div className={styles.restrictedContainer}>Loading user info...</div>
      </div>
    );
  }

  return (
    <div className={styles.chatContainer}>
      {!isAccessible ? (
        <div className={styles.restrictedContainer}>
          <h3>Meeting is Restricted</h3>
          <p>
            This Meeting requires a membership to access. Please purchase one
            below.
          </p>

          <button
            onClick={handleMembershipClick}
            className={styles.membershipLink}
            disabled={isPurchasing || isLoadingPlans}
          >
            {isLoadingPlans ? (
              <>
                <FaSpinner className={styles.spinner} />
                Loading Plans...
              </>
            ) : isPurchasing ? (
              <>
                <FaSpinner className={styles.spinner} />
                Processing...
              </>
            ) : (
              "Buy Membership"
            )}
          </button>

          {/* Plan Selection Modal */}
          {showPlanSelection && (
            <div className={styles.modalOverlay}>
              <div className={styles.modalContent}>
                <h3>Choose a Membership Plan</h3>
                <p>This meeting offers multiple membership options:</p>

                <div className={styles.plansList}>
                  {availablePlans.map((plan) => (
                    <div key={plan.id} className={styles.planCard}>
                      <div className={styles.planInfo}>
                        <h4>{plan.name || plan.label || `Plan ${plan.id}`}</h4>
                        <div className={styles.planPrice}>
                          {plan.payment_type === "free"
                            ? "Free"
                            : `$${plan.price}`}
                        </div>
                        {plan.short_description && (
                          <p className={styles.planDescription}>
                            {plan.short_description}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          handlePurchaseMembership(plan.id);
                          setShowPlanSelection(false);
                        }}
                        className={styles.planSelectButton}
                        disabled={isPurchasing}
                      >
                        {isPurchasing ? (
                          <>
                            <FaSpinner className={styles.spinner} />
                            Processing...
                          </>
                        ) : (
                          "Select This Plan"
                        )}
                      </button>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => setShowPlanSelection(false)}
                  className={styles.cancelButton}
                  disabled={isPurchasing}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      ) : !joined ? (
        <div className={styles.restrictedContainer}>
          <h3>Zoom Meeting</h3>
          <p>Click the button below to join.</p>
          <button onClick={handleJoinMeeting} className={styles.membershipLink}>
            Join Meeting
          </button>
        </div>
      ) : (
        <div />
      )}
    </div>
  );
};

export default ZoomMeetingCard;
